/**
 * Home2 Demo Section
 * Embedded Flatland Trap interactive story.
 * 
 * @module components/home2/Home2Demo
 */

import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { flatlandTrapTwee } from '../../data/flatlandTrap';
import { compileFlatlandStory } from '../../utils/flatlandCompiler';

const Home2Demo = () => {
  const [expanded, setExpanded] = useState(false);
  const [compiledHtml, setCompiledHtml] = useState('');

  useEffect(() => {
    const html = compileFlatlandStory(flatlandTrapTwee);
    setCompiledHtml(html);
  }, []);

  useEffect(() => {
    if (compiledHtml && expanded) {
      const scriptMatch = compiledHtml.match(/<script>([\s\S]*?)<\/script>/);
      if (scriptMatch) {
        try {
          const scriptFunc = new Function(scriptMatch[1]);
          scriptFunc();
        } catch (error) {
          console.error('Demo script error:', error);
        }
      }
    }
  }, [compiledHtml, expanded]);

  return (
    <section className="container mx-auto px-4 py-12">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all text-left"
          aria-expanded={expanded}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Sparkles className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-1">
                  Experience It Yourself: The Flatland Trap
                </h2>
                <p className="text-sm text-slate-600">
                  An interactive story that proves why engagement matters. Click to try it.
                </p>
              </div>
            </div>
            {expanded ? (
              <ChevronUp className="h-6 w-6 text-indigo-400 flex-shrink-0" />
            ) : (
              <ChevronDown className="h-6 w-6 text-indigo-400 flex-shrink-0" />
            )}
          </div>
        </button>
        
        {expanded && (
          <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-lg">
            <div className="p-4 bg-slate-50 border-b border-slate-200">
              <p className="text-sm text-slate-600">
                <strong>Note:</strong> This is an interactive demo. Make choices to see how the story adapts.
              </p>
            </div>
            <div 
              className="p-6"
              dangerouslySetInnerHTML={{ __html: compiledHtml.replace(/<script>[\s\S]*?<\/script>/g, '') }}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default Home2Demo;
